import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    TextField,
    Typography,
    Button
} from '@mui/material';
import * as Icons from '@mui/icons-material';

// Häufig verwendete Icons für LMS/Media
const commonIcons = [
    'Folder', 'FolderOpen', 'Description', 'Article', 'Notes',
    'Image', 'VideoLibrary', 'AudioFile', 'PictureAsPdf', 'InsertDriveFile',
    'School', 'MenuBook', 'Book', 'LibraryBooks', 'AutoStories',
    'Assignment', 'Assessment', 'Quiz', 'Task', 'Checklist',
    'Code', 'DataObject', 'Terminal', 'Computer', 'Laptop',
    'Science', 'Calculate', 'Functions', 'Psychology', 'Biotech',
    'Language', 'Translate', 'Public', 'Travel', 'Map',
    'Business', 'Work', 'Group', 'Person', 'People',
    'Category', 'Label', 'Bookmark', 'Star', 'Favorite',
    'CloudUpload', 'CloudDownload', 'Cloud', 'Storage', 'Archive',
    'PlayCircle', 'PlayArrow', 'Movie', 'Theaters', 'LiveTv',
    'Headphones', 'MusicNote', 'Album', 'Radio', 'Mic',
    'Palette', 'Brush', 'ColorLens', 'Draw', 'Create',
    'Build', 'Construction', 'Handyman', 'Engineering', 'Settings'
];

interface IconPickerProps {
    value?: string;
    onChange: (value: string) => void;
    label?: string;
}

const IconPicker = ({ value, onChange, label }: IconPickerProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleSelect = (iconName: string) => {
        onChange(iconName);
        handleClose();
    };

    // Filter icons based on search
    const filteredIcons = commonIcons.filter(icon =>
        icon.toLowerCase().includes(search.toLowerCase())
    );

    // Get the selected icon component
    const SelectedIconComponent = value && (Icons as any)[value]
        ? (Icons as any)[value]
        : Icons.HelpOutline;

    return (
        <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {label || 'Icon'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box
                    onClick={handleOpen}
                    sx={{
                        width: 80,
                        height: 80,
                        border: '2px dashed #ccc',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: '#1cab7e',
                            backgroundColor: '#f5f5f5'
                        }
                    }}
                >
                    <SelectedIconComponent sx={{ fontSize: 40, color: '#666' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                        {value || 'Kein Icon'}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpen}
                        sx={{ mt: 1 }}
                    >
                        Icon auswählen
                    </Button>
                </Box>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Icon auswählen</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        placeholder="Icon suchen..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ mb: 2 }}
                        autoFocus
                    />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                            gap: 1,
                            maxHeight: 400,
                            overflow: 'auto'
                        }}
                    >
                        {filteredIcons.map((iconName) => {
                            const IconComponent = (Icons as any)[iconName];
                            if (!IconComponent) return null;

                            return (
                                <IconButton
                                    key={iconName}
                                    onClick={() => handleSelect(iconName)}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '1',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        border: value === iconName ? '2px solid #1cab7e' : '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        backgroundColor: value === iconName ? '#e8f5e9' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                            borderColor: '#1cab7e'
                                        }
                                    }}
                                >
                                    <IconComponent sx={{ fontSize: 24 }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.65rem',
                                            textAlign: 'center',
                                            lineHeight: 1,
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {iconName}
                                    </Typography>
                                </IconButton>
                            );
                        })}
                    </Box>
                    {filteredIcons.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="textSecondary">
                                Keine Icons gefunden für "{search}"
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default IconPicker;
